import { Box, Stack, Typography, useMediaQuery, useTheme } from "@mui/material"
import type { FC } from "react"
import { useTranslation } from "react-i18next"

import v4PoleDesktopEN from "images/devices/position/diagrams/desktop/sentri-4.0-pole-tower-static-view-EN-desktop.png"
import v4PoleMobileEN from "images/devices/position/diagrams/mobile/sentri-4.0-pole-tower-static-view-EN-mobile.png"
import v4PoleDesktopES from "images/devices/position/diagrams/desktop/sentri-4.0-pole-tower-static-view-ES-desktop.png"
import v4PoleMobileES from "images/devices/position/diagrams/mobile/sentri-4.0-pole-tower-static-view-ES-mobile.png"
import v4LegacyPoleDesktopEN from "images/devices/position/diagrams/desktop/sentri-4.0-legacy-pole-tower-static-view-EN-desktop.png"
import v4LegacyPoleMobileEN from "images/devices/position/diagrams/mobile/sentri-4.0-legacy-pole-tower-static-view-EN-mobile.png"
import v4LegacyPoleDesktopES from "images/devices/position/diagrams/desktop/sentri-4.0-legacy-pole-tower-static-view-ES-desktop.png"
import v4LegacyPoleMobileES from "images/devices/position/diagrams/mobile/sentri-4.0-legacy-pole-tower-static-view-ES-mobile.png"
import v2v3TowerDesktop from "images/devices/position/diagrams/desktop/v2-v3-tower-static-view-desktop.png"
import v2v3TowerMobile from "images/devices/position/diagrams/mobile/v2-v3-tower-static-view-mobile.png"
import v4TowerDesktopEN from "images/devices/position/diagrams/desktop/sentri-4.0-tower-static-view-EN-desktop.png"
import v4TowerMobileEN from "images/devices/position/diagrams/mobile/sentri-4.0-tower-static-view-EN-mobile.png"
import v4TowerDesktopES from "images/devices/position/diagrams/desktop/sentri-4.0-tower-static-view-ES-desktop.png"
import v4TowerMobileES from "images/devices/position/diagrams/mobile/sentri-4.0-tower-static-view-ES-mobile.png"
import v4LegacyTowerDesktopEN from "images/devices/position/diagrams/desktop/sentri-4.0-legacy-tower-static-view-EN-desktop.png"
import v4LegacyTowerMobileEN from "images/devices/position/diagrams/mobile/sentri-4.0-legacy-tower-static-view-EN-mobile.png"
import v4LegacyTowerDesktopES from "images/devices/position/diagrams/desktop/sentri-4.0-legacy-tower-static-view-ES-desktop.png"
import v4LegacyTowerMobileES from "images/devices/position/diagrams/mobile/sentri-4.0-legacy-tower-static-view-ES-mobile.png"
import v4HVATowerDesktopEN from "images/devices/position/diagrams/desktop/sentri-4.0-HVA-tower-static-view-EN-desktop.png"
import v4HVATowerMobileEN from "images/devices/position/diagrams/mobile/sentri-4.0-HVA-tower-static-view-EN-mobile.png"
import v4HVATowerDesktopES from "images/devices/position/diagrams/desktop/sentri-4.0-HVA-tower-static-view-ES-desktop.png"
import v4HVATowerMobileES from "images/devices/position/diagrams/mobile/sentri-4.0-HVA-tower-static-view-ES-mobile.png"
import v4MastTowerDesktopEN from "images/devices/position/diagrams/desktop/sentri-4.0-MAST-tower-static-view-EN-desktop.png"
import v4MastTowerMobileEN from "images/devices/position/diagrams/mobile/sentri-4.0-MAST-tower-static-view-EN-mobile.png"
import v4MastTowerDesktopES from "images/devices/position/diagrams/desktop/sentri-4.0-MAST-tower-static-view-ES-desktop.png"
import v4MastTowerMobileES from "images/devices/position/diagrams/mobile/sentri-4.0-MAST-tower-static-view-ES-mobile.png"
import type { TowerPositionVariant } from "types/devicePosition.types"
import PopUp from "widgets/common/PopUp"

interface TowerViewDialogProps {
  open: boolean
  onClose: () => void
  variant: TowerPositionVariant
}

type Viewport = "desktop" | "mobile"
type LocalizedImages = Record<string, { desktop: string; mobile: string }>

type DialogConfig = {
  images: LocalizedImages
  descriptionKey: string
  titleKey: string
}

const DIALOG_CONFIG: Record<TowerPositionVariant, DialogConfig> = {
  v2v3: {
    images: {
      en: { desktop: v2v3TowerDesktop, mobile: v2v3TowerMobile },
      es: { desktop: v2v3TowerDesktop, mobile: v2v3TowerMobile },
    },
    descriptionKey: "plots_descriptions.DEVICE_POSITION.LAST_POSITION_TOWER_V2_V3",
    titleKey: "plots_descriptions.LEGACY_TOWER_STATIC_VIEW",
  },
  v4: {
    images: {
      en: { desktop: v4TowerDesktopEN, mobile: v4TowerMobileEN },
      es: { desktop: v4TowerDesktopES, mobile: v4TowerMobileES },
    },
    descriptionKey: "plots_descriptions.DEVICE_POSITION.LAST_POSITION_TOWER_V4",
    titleKey: "plots_descriptions.TOWER_STATIC_VIEW",
  },
  "v4-legacy": {
    images: {
      en: { desktop: v4LegacyTowerDesktopEN, mobile: v4LegacyTowerMobileEN },
      es: { desktop: v4LegacyTowerDesktopES, mobile: v4LegacyTowerMobileES },
    },
    descriptionKey: "plots_descriptions.DEVICE_POSITION.LAST_POSITION_TOWER_V4_LEGACY",
    titleKey: "plots_descriptions.LEGACY_TOWER_STATIC_VIEW",
  },
  "v4-pole": {
    images: {
      en: { desktop: v4PoleDesktopEN, mobile: v4PoleMobileEN },
      es: { desktop: v4PoleDesktopES, mobile: v4PoleMobileES },
    },
    descriptionKey: "plots_descriptions.DEVICE_POSITION.LAST_POSITION_TOWER_HEALTH",
    titleKey: "plots_descriptions.TOWER_STATIC_VIEW",
  },
  "v4-pole-legacy": {
    images: {
      en: { desktop: v4LegacyPoleDesktopEN, mobile: v4LegacyPoleMobileEN },
      es: { desktop: v4LegacyPoleDesktopES, mobile: v4LegacyPoleMobileES },
    },
    descriptionKey: "plots_descriptions.DEVICE_POSITION.LAST_POSITION_TOWER_POLE_V4",
    titleKey: "plots_descriptions.LEGACY_TOWER_STATIC_VIEW",
  },
  "v4-hva": {
    images: {
      en: { desktop: v4HVATowerDesktopEN, mobile: v4HVATowerMobileEN },
      es: { desktop: v4HVATowerDesktopES, mobile: v4HVATowerMobileES },
    },
    descriptionKey: "plots_descriptions.DEVICE_POSITION.LAST_POSITION_TOWER_HEALTH",
    titleKey: "plots_descriptions.TOWER_STATIC_VIEW",
  },
  "v4-mast": {
    images: {
      en: { desktop: v4MastTowerDesktopEN, mobile: v4MastTowerMobileEN },
      es: { desktop: v4MastTowerDesktopES, mobile: v4MastTowerMobileES },
    },
    descriptionKey: "plots_descriptions.DEVICE_POSITION.LAST_POSITION_TOWER_HEALTH",
    titleKey: "plots_descriptions.TOWER_STATIC_VIEW",
  },
}

function getImage(images: LocalizedImages, lang: string, viewport: Viewport): string {
  return (images[lang] ?? images.en)[viewport]
}

export const TowerViewDialog: FC<TowerViewDialogProps> = ({ open, onClose, variant }) => {
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const lang = i18n.resolvedLanguage?.startsWith("es") ? "es" : "en"
  const viewport: Viewport = isMobile ? "mobile" : "desktop"
  const config = DIALOG_CONFIG[variant]

  const imgSrc = getImage(config.images, lang, viewport)

  return (
    <PopUp
      open={open}
      onClose={onClose}
      titleKey={config.titleKey}
      maxWidth={"md"}
      fullWidth
    >
      <Stack gap={1}>
        <Typography sx={{ whiteSpace: "pre-line" }}>
          {t(config.descriptionKey)}
        </Typography>
        <Box
          sx={{
            position: "relative",
            width: variant !== "v2v3" && !isMobile ? "100%" : "90%",
          }}
          alignSelf="center"
        >
          <Box component="img" src={imgSrc} width="100%" />
        </Box>
      </Stack>
    </PopUp>
  )
}
